import type { I18n } from '@lingui/core';

/**
 * Check if content is a semantic tool content key
 */
export function isToolContentKey(content: string): boolean {
  return content?.startsWith('tool.');
}

/**
 * Get localized content for tool responses
 * Provides fallback translations for semantic content keys
 * @param content - The content string to translate
 * @param responseType - The response type for fallback logic
 * @param i18n - The Lingui i18n instance (from useLingui hook)
 */
export function getToolContent(
  content: string,
  responseType: string | undefined,
  i18n: I18n,
): string {
  // Handle semantic keys
  if (isToolContentKey(content)) {
    switch (content) {
      case 'tool.membership.login.required':
        return i18n._('Please log in to access your membership information.');
      case 'tool.membership.registration.prompt':
        return i18n._("Let's get you registered.");
      case 'tool.membership.status.loading':
        return i18n._('Loading membership information...');
      case 'tool.membership.status.expired':
        return i18n._(
          'Your membership has expired. You can purchase a new one below.',
        );
      case 'tool.membership.profile.loading':
        return i18n._('Loading your profile...');
      case 'tool.auth.required':
        return i18n._('Please complete authentication to continue.');
      case 'tool.groupClass.auth.required':
        return i18n._('Please log in to view group class information.');

      // Membership disabled feature messages
      case 'tool.membership.disabled.status':
        return i18n._(
          'Viewing membership status is disabled for this chat. Please contact support if you need this enabled.',
        );
      case 'tool.membership.disabled.profile':
        return i18n._(
          'Viewing profile information is disabled for this chat. Please contact support if you need this enabled.',
        );
      case 'tool.membership.disabled.registration':
        return i18n._(
          'Membership registration is disabled for this chat. Please visit the front desk or contact support.',
        );
      case 'tool.membership.disabled.pause':
        return i18n._(
          'Pausing membership is disabled for this chat. Please contact your club reception to request a pause.',
        );
      case 'tool.membership.disabled.generic':
        return i18n._(
          'This membership action is disabled for this chat. Please contact support for assistance.',
        );

      default:
        return i18n._('Processing your request...');
    }
  }

  // Handle empty content with type-based fallbacks
  if (!content && responseType) {
    switch (responseType) {
      case 'membership_login':
        return i18n._('Authentication required for membership access.');
      case 'membership_status':
        return i18n._('Loading membership information...');
      case 'membership_registration':
        return i18n._('Registration information needed.');
      case 'user_profile':
        return i18n._('Loading your profile...');

      default:
        return i18n._('Processing your request...');
    }
  }

  // Return content as-is (for agent-generated messages)
  return content || '';
}
